import { useState, useRef } from "react";
import { FiX, FiCamera, FiLogOut, FiStar, FiUserMinus, FiEdit2,
         FiCheck, FiUserPlus, FiSave } from "react-icons/fi";
import toast from "react-hot-toast";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials } from "../../utils/helpers";
import AddMemberModal from "./AddMemberModal";
import ImageCropModal from "./ImageCropModal";
import styles from "./GroupInfoPanel.module.css";

const GroupInfoPanel = ({ onClose }) => {
  const { user } = useAuthStore();
  const { activeConversation, updateGroup, removeGroupMember, promoteToAdmin, leaveGroup } = useChatStore();
  const socketRef = useSocket();
  const fileRef = useRef(null);

  const [editMode,      setEditMode]      = useState(false);
  const [groupName,     setGroupName]     = useState(activeConversation?.groupName || "");

  const [avatarData,    setAvatarData]    = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(activeConversation?.groupAvatar || "");
  const [saving,        setSaving]        = useState(false);
  const [confirmLeave,  setConfirmLeave]  = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);  // memberId
  const [confirmPromote,setConfirmPromote]= useState(null);  // { id, name }
  const [showAddMember, setShowAddMember] = useState(false);
  const [cropSrc,       setCropSrc]       = useState(null);

  const adminId      = activeConversation?.groupAdmin?._id || activeConversation?.groupAdmin;
  const groupAdmins  = (activeConversation?.groupAdmins || []).map((a) => a?._id || a);
  const isAdmin      = adminId === user._id || groupAdmins.includes(user._id);
  const participants = activeConversation?.participants || [];

  // ── Avatar change — opens crop modal like profile ─────────────
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropSrc(reader.result);
      setEditMode(true);
    };
    reader.readAsDataURL(file);
    e.target.value = ""; // reset so same file can be re-selected
  };

  const handleCropConfirm = (croppedBase64) => {
    setAvatarPreview(croppedBase64);
    setAvatarData(croppedBase64);
    setCropSrc(null);
  };

  // ── Save group info ───────────────────────────────────────────
  const handleSave = async () => {
    if (!groupName.trim()) return toast.error("Group name cannot be empty");
    setSaving(true);
    try {
      const payload = { groupName: groupName.trim() };
      if (avatarData) payload.groupAvatar = avatarData;
      const updated = await updateGroup(activeConversation._id, payload);
      socketRef?.current?.emit("group:update", {
        conversationId: activeConversation._id,
        conversation: updated,
      });
      toast.success("Group updated!");
      setEditMode(false);
      setAvatarData(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update group");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setGroupName(activeConversation?.groupName || "");
    setAvatarPreview(activeConversation?.groupAvatar || "");
    setAvatarData(null);
  };

  // ── Remove member ─────────────────────────────────────────────
  const handleRemove = async (memberId) => {
    setConfirmRemove(null);
    try {
      const updated = await removeGroupMember(activeConversation._id, memberId);
      socketRef?.current?.emit("group:update", {
        conversationId: activeConversation._id,
        conversation: updated,
      });
      toast.success("Member removed");
    } catch { toast.error("Failed to remove member"); }
  };

  // ── Promote to admin ──────────────────────────────────────────
  const handlePromote = async () => {
    const { id, name } = confirmPromote;
    setConfirmPromote(null);
    try {
      const updated = await promoteToAdmin(activeConversation._id, id);
      // Broadcast updated conversation to all members
      socketRef?.current?.emit("group:update", {
        conversationId: activeConversation._id,
        conversation: updated,
      });
      // Notify ALL members: "Aman made Aakash an admin"
      // promotedUserId helps the promoted person show different text
      socketRef?.current?.emit("group:admin_promoted", {
        conversationId: activeConversation._id,
        promotedUserId: id,
        promotedName: name,
        promotedBy: user.name,
        groupName: activeConversation?.groupName || "the group",
        conversation: updated,
      });
      toast.success(`${name} is now an admin 👑`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to promote");
    }
  };

  // ── Leave group ───────────────────────────────────────────────
  const handleLeave = async () => {
    setConfirmLeave(false);
    try {
      const result = await leaveGroup(activeConversation._id);
      if (result?.groupDeleted) {
        // Group is gone — notify remaining (none) and close
        socketRef?.current?.emit("group:deleted", {
          conversationId: activeConversation._id,
        });
        toast.success("You were the last member. Group deleted.");
      } else if (result?.newAdminId) {
        // Admin left → new admin was auto-assigned
        socketRef?.current?.emit("group:leave", {
          conversationId: activeConversation._id,
          userId: user._id,
          userName: user.name,
          newAdminId: result.newAdminId,
          conversation: result.conversation,
        });
        socketRef?.current?.emit("group:update", {
          conversationId: activeConversation._id,
          conversation: result.conversation,
        });
        toast.success("Left the group. Admin transferred automatically.");
      } else {
        socketRef?.current?.emit("group:leave", {
          conversationId: activeConversation._id,
          userId: user._id,
          userName: user.name,
        });
        toast.success("Left the group");
      }
      onClose();
    } catch { toast.error("Failed to leave group"); }
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>Group info</span>
        <button className={styles.closeBtn} onClick={onClose}><FiX size={18} /></button>
      </div>

      {/* ── Hero: avatar + name ── */}
      <div className={styles.heroSection}>
        <div className={styles.avatarWrap}>
          <div className={styles.avatar}>
            {avatarPreview
              ? <img src={avatarPreview} alt="group" className={styles.avatarImg} />
              : <span className={styles.avatarInitials}>{getInitials(groupName)}</span>}
          </div>
          {isAdmin && (
            <button className={styles.cameraBtn} onClick={() => fileRef.current?.click()}
              title="Change group photo">
              <FiCamera size={14} />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*"
            style={{ display: "none" }} onChange={handleAvatarChange} />
        </div>

        {/* Name row: view or edit */}
        {editMode && isAdmin ? (
          <div className={styles.editNameWrap}>
            <input
              className={styles.nameInput}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") handleCancelEdit(); }}
              placeholder="Group name"
            />
          </div>
        ) : (
          <div className={styles.nameRow}>
            <span className={styles.groupName}>{groupName}</span>
            {isAdmin && (
              <button className={styles.editNameBtn} onClick={() => setEditMode(true)} title="Edit group">
                <FiEdit2 size={14} />
              </button>
            )}
          </div>
        )}

        <div className={styles.groupMetaRow}>
          <span className={styles.groupMeta}>Group · {participants.length} members</span>
        </div>

        {/* Save / Cancel buttons — only in edit mode */}
        {editMode && isAdmin && (
          <div className={styles.editActions}>
            <button className={styles.cancelEditBtn} onClick={handleCancelEdit} disabled={saving}>
              Cancel
            </button>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><FiSave size={13} /> Save</>}
            </button>
          </div>
        )}
      </div>

      {/* ── Members list ── */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>{participants.length} members</div>
        <div className={styles.memberList}>
          {participants.map((p) => {
            const pId        = p._id || p;
            const pName      = p.name || "Member";
            const isThisAdmin = pId === adminId || groupAdmins.includes(pId);
            const isSelf     = pId === user._id;
            return (
              <div key={pId} className={styles.member}>
                <div className={styles.memberAvatar}>
                  {p.avatar
                    ? <img src={p.avatar} alt={pName} className={styles.memberAvatarImg} />
                    : <span className={styles.memberInitials}>{getInitials(pName)}</span>}
                  {p.isOnline && <span className={styles.onlineDot} />}
                </div>
                <div className={styles.memberInfo}>
                  <div className={styles.memberNameRow}>
                    <span className={styles.memberName}>{isSelf ? "You" : pName}</span>
                    {isThisAdmin && <span className={styles.adminBadge}>Group admin</span>}
                  </div>
                  {p.bio && <span className={styles.memberBio}>{p.bio}</span>}
                </div>

                {/* Actions: only admin sees, only for non-self non-admin members */}
                {isAdmin && !isSelf && !isThisAdmin && (
                  <div className={styles.memberActions}>
                    <button
                      className={styles.memberBtn}
                      onClick={() => setConfirmPromote({ id: pId, name: pName })}
                      title="Make admin"
                    >
                      <FiStar size={13} />
                    </button>
                    <button
                      className={`${styles.memberBtn} ${styles.dangerBtn}`}
                      onClick={() => setConfirmRemove(pId)}
                      title="Remove member"
                    >
                      <FiUserMinus size={13} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Add member ── */}
      {isAdmin && (
        <div className={styles.addMemberRow}>
          <button className={styles.addMemberBtn} onClick={() => setShowAddMember(true)}>
            <div className={styles.addMemberIcon}><FiUserPlus size={18} /></div>
            <span>Add member</span>
          </button>
        </div>
      )}

      {/* ── Leave group ── */}
      <div className={styles.leaveSection}>
        <button
          className={styles.leaveBtn}
          onClick={() => setConfirmLeave(true)}
          title="Leave group"
        >
          <FiLogOut size={16} />
          <span>Leave group</span>
        </button>
      </div>

      {/* ── Confirm: Promote to admin ── */}
      {confirmPromote && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmBox}>
            <h4 className={styles.confirmTitle}>Make admin?</h4>
            <p className={styles.confirmText}>
              Make <strong>{confirmPromote?.name}</strong> an admin of this group?
            </p>
            <div className={styles.confirmBtns}>
              <button className={styles.confirmCancel} onClick={() => setConfirmPromote(null)}>Cancel</button>
              <button className={styles.confirmPrimary} onClick={handlePromote}>Make Admin</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm: Remove member ── */}
      {confirmRemove && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmBox}>
            <h4 className={styles.confirmTitle}>Remove member?</h4>
            <p className={styles.confirmText}>This member will be removed from the group.</p>
            <div className={styles.confirmBtns}>
              <button className={styles.confirmCancel} onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button className={styles.confirmDanger} onClick={() => handleRemove(confirmRemove)}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm: Leave group ── */}
      {confirmLeave && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmBox}>
            <h4 className={styles.confirmTitle}>Leave group?</h4>
            <p className={styles.confirmText}>
              {isAdmin
                ? "You are the admin. If you leave, admin will be automatically transferred to a random member."
                : "You will no longer receive messages from this group."}
            </p>
            <div className={styles.confirmBtns}>
              <button className={styles.confirmCancel} onClick={() => setConfirmLeave(false)}>Cancel</button>
              <button className={styles.confirmDanger} onClick={handleLeave}>Leave</button>
            </div>
          </div>
        </div>
      )}

      {showAddMember && <AddMemberModal onClose={() => setShowAddMember(false)} />}

      {cropSrc && (
        <ImageCropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropSrc(null)}
        />
      )}
    </div>
  );
};

export default GroupInfoPanel;
